import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useTranslations } from 'next-intl';

import { Button } from '@/components/button';
import IntentLink from '@/components/IntentLink';
import { chevronDown as IconChevronDown } from '@/components/icons/chevronDown';
import { chevronUp as IconChevronUp } from '@/components/icons/chevronUp';
import { VisuallyHidden } from '@/components/visually-hidden';
import { FEATURES } from '@/constants';
import { useQuantaLogin } from '@/hooks/api/quanta/useQuantaLogin';
import { cn } from '@/utils/cn';

import { sidebarData } from '../../sidebar/data';
import { getIsQuantaPage, isSidebarItemActive } from '../../utils';

interface DropdownNavProps {
  pathname: string;
}

const DropdownNav: React.FC<DropdownNavProps> = ({ pathname }: DropdownNavProps) => {
  const t = useTranslations('layout');
  const [isOpen, setIsOpen] = useState<boolean>(false);

  return (
    <Dialog.Root open={isOpen} onOpenChange={setIsOpen}>
      <Dialog.Trigger asChild>
        <Button
          size="small"
          variant="tertiary"
          className="rounded-full shrink-0 w-6! h-6! bg-surface-secondary [&_svg]:size-4"
          aria-label={t('dropdownNav.title')}
        >
          {isOpen ? <IconChevronUp /> : <IconChevronDown />}
        </Button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Content className="fixed left-0 top-[74px] w-[250px] bg-surface-secondary border border-border-secondary outline-hidden z-50">
          <VisuallyHidden asChild>
            <Dialog.Title>{t('dropdownNav.title')}</Dialog.Title>
          </VisuallyHidden>
          <VisuallyHidden asChild>
            <Dialog.Description>{t('dropdownNav.description')}</Dialog.Description>
          </VisuallyHidden>
          <NavigationItems pathname={pathname} close={() => setIsOpen(false)} />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

function NavigationItems({ pathname, close }: DropdownNavProps & { close: () => void }) {
  const t = useTranslations('layout');
  const { data: loginData } = useQuantaLogin({ enabled: false });
  const isNavigationEnabled = !!loginData?.data.user.user_id;
  const handleLinkClick = (_e: React.MouseEvent<HTMLAnchorElement>) => {
    close();
  };

  const handleSubLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, parentHref: string) => {
    const isQuantaParent = getIsQuantaPage(parentHref);

    if (isQuantaParent && !isNavigationEnabled) {
      e.preventDefault();

      return;
    }

    close();
  };

  return (
    <div className="flex flex-col py-[10px]">
      {sidebarData.map(({ href, labelKey, subItems, icon }) => {
        const isDisabled = (href === '/liquidations' && !FEATURES.liquidations) || (href === '/swap' && !FEATURES.swap);
        const isActive = isSidebarItemActive(pathname, href);
        const isQuantaParent = getIsQuantaPage(href);
        const isRootActive = pathname === href;

        if (isDisabled) {
          return (
            <div
              key={href}
              className={cn('flex items-center gap-3 px-8 py-3 font-medium text-text-secondary leading-18 cursor-not-allowed')}
            >
              {icon}
              {t(labelKey)}
            </div>
          );
        }

        return (
          <div key={href} className="flex flex-col">
            <IntentLink
              href={href}
              className={cn(
                'flex items-center justify-between w-full px-8 py-3 text-left font-medium text-sidebar-inactive leading-18 hover:text-highlight transition-colors',
                {
                  'text-highlight cursor-default hover:text-highlight font-bold': pathname === href,
                },
              )}
              onClick={handleLinkClick}
            >
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center min-w-[26px]">{icon}</div>
                <span>{t(labelKey)}</span>
              </div>
              {subItems &&
                (isActive ? (
                  <IconChevronDown
                    className={cn('size-4', { 'text-text-primary': isRootActive, 'text-sidebar-chevron-inactive': !isRootActive })}
                  />
                ) : (
                  <IconChevronUp className="size-4 text-sidebar-chevron-inactive" />
                ))}
            </IntentLink>
            {subItems && isActive && (
              <div className="flex flex-col ml-[38px]">
                {subItems.map(({ labelKey: subLabelKey, href: subHref }) => {
                  const isDisabledSub =
                    (isQuantaParent && !isNavigationEnabled) ||
                    (href === '/overview/reserve' && !FEATURES.reserve) ||
                    subHref === '/overview/governance';

                  return isDisabledSub ? (
                    <div
                      key={subHref}
                      className={cn('w-full px-8 py-3 text-left font-medium text-text-primary/50 leading-18 cursor-not-allowed')}
                    >
                      {t(subLabelKey)}
                    </div>
                  ) : (
                    <IntentLink
                      key={subHref}
                      href={subHref}
                      className={cn(
                        'w-full px-8 py-3 text-left font-medium text-sidebar-inactive leading-18 hover:text-highlight transition-colors',
                        {
                          'text-highlight cursor-default hover:text-highlight font-bold': pathname === subHref,
                        },
                      )}
                      onClick={(e) => handleSubLinkClick(e, href)}
                    >
                      {t(subLabelKey)}
                    </IntentLink>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default DropdownNav;
